/**
 * Domain Error Page
 *
 * Shown when accessing an invalid, unverified, or misconfigured custom domain.
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Globe, ArrowRight } from "lucide-react";

interface DomainErrorProps {
  error: string;
  hostname?: string;
}

export function DomainError({ error, hostname }: DomainErrorProps) {
  const mainAppUrl = "https://trainerpulse.app";

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30 p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-14 h-14 rounded-full bg-warning/10 flex items-center justify-center mb-4">
            <AlertTriangle className="h-7 w-7 text-warning" />
          </div>
          <CardTitle className="text-xl">Domain Not Found</CardTitle>
          <CardDescription className="text-base mt-2">{error}</CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {hostname && (
            <div className="bg-muted rounded-lg p-3 flex items-center gap-3">
              <Globe className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Requested domain</p>
                <p className="font-mono text-sm truncate">{hostname}</p>
              </div>
            </div>
          )}

          <div className="space-y-3 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">This could mean:</p>
            <ul className="space-y-2 list-disc list-inside">
              <li>The domain hasn't been configured yet</li>
              <li>DNS verification is still pending</li>
              <li>The domain was removed or expired</li>
              <li>There's a typo in the URL</li>
            </ul>
          </div>

          <div className="pt-2 space-y-3">
            <Button asChild className="w-full">
              <a href={mainAppUrl}>
                Go to TrainerPulse
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              If you're a trainer setting up your custom domain,{" "}
              <a
                href={`${mainAppUrl}/company/settings`}
                className="text-primary hover:underline"
              >
                check your domain settings
              </a>
              .
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default DomainError;
